import React, { useEffect, useRef } from 'react';
import { Button, Input, Popover, Space, Typography } from 'antd';
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  CloseOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useMyContext } from 'Context/MyContextProvider';

const { Text } = Typography;

const DESKTOP_POPUP_WIDTH = 420;

const ChatMessageSearchPopup = ({
  open,
  query,
  onQueryChange,
  statusText,
  onPrevious,
  onNext,
  onClose,
  onSearchFull,
  showSearchFull,
  searching,
  loadingContext,
  canGoPrevious = false,
  canGoNext = false,
  style,
  fullWidth = false,
}) => {
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => inputRef.current?.focus(), 0);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [open]);

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (canGoNext) {
        onNext();
      }
      return;
    }
    if (event.key === 'Enter' && event.shiftKey) {
      event.preventDefault();
      if (canGoPrevious) {
        onPrevious();
      }
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
    }
  };

  return (
    <div
      style={{
        width: fullWidth ? '100%' : DESKTOP_POPUP_WIDTH,
        maxWidth: fullWidth ? '100%' : 'min(420px, 80vw)',
        ...style,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'stretch',
          width: '100%',
        }}
      >
        <Input
          ref={inputRef}
          allowClear
          placeholder="Search in conversation"
          value={query}
          onChange={(event) => onQueryChange(event.target.value)}
          onKeyDown={handleKeyDown}
          style={{
            flex: 1,
            minWidth: 0,
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,
          }}
        />
        <Space.Compact>
          <Button
            icon={<ArrowUpOutlined />}
            aria-label="Previous match"
            disabled={!canGoPrevious}
            loading={searching || loadingContext}
            onClick={onPrevious}
          />
          <Button
            icon={<ArrowDownOutlined />}
            aria-label="Next match"
            disabled={!canGoNext}
            loading={searching || loadingContext}
            onClick={onNext}
          />
          <Button icon={<CloseOutlined />} aria-label="Close search" onClick={onClose} />
        </Space.Compact>
      </div>
      {!fullWidth && (
        <>
          <Text
            type="secondary"
            style={{
              display: 'block',
              fontSize: 12,
              lineHeight: '18px',
              marginTop: 6,
              minHeight: 18,
            }}
          >
            {statusText}
          </Text>
          {showSearchFull && (
            <Button
              type="link"
              size="small"
              style={{ paddingLeft: 0, marginTop: 2, height: 'auto' }}
              onClick={onSearchFull}
            >
              Search full conversation
            </Button>
          )}
        </>
      )}
    </div>
  );
};

export default ChatMessageSearchPopup;

export const ChatMessageSearchTrigger = ({
  open,
  onOpenChange,
  children,
  ...popupProps
}) => {
  const { isMobile } = useMyContext();

  return (
    <Popover
      trigger={isMobile ? [] : 'click'}
      placement="bottomRight"
      open={isMobile ? false : open}
      onOpenChange={isMobile ? undefined : onOpenChange}
      destroyTooltipOnHide
      overlayInnerStyle={{ padding: 12 }}
      content={<ChatMessageSearchPopup open={open} {...popupProps} />}
    >
      {children}
    </Popover>
  );
};

export const ChatMessageSearchIcon = ({ onClick, className = 'cursor-pointer' }) => (
  <SearchOutlined className={className} style={{ fontSize: 16 }} onClick={onClick} />
);
